import { Loader } from '@/shared/ui/loader'

import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'

import type { PageListLoaderProps } from './Page.ListLoader.types'

/**
 * Page.ListLoader - Infinite scroll trigger with loading indicator
 *
 * Component used at the bottom of lists to trigger infinite scroll and display
 * a loading state. The ref is used by an intersection observer to detect when
 * the trigger element enters the viewport.
 *
 * @example
 * // Basic usage with intersection observer
 * const { fetchNextTriggerRef, isFetchingNextPage } = useGetProjectsData()
 *
 * <Page.ListLoader
 *   ref={fetchNextTriggerRef}
 *   isFetching={isFetchingNextPage}
 *   label={t('common.list.loadingMore')}
 * />
 *
 * @example
 * // Without label
 * <Page.ListLoader ref={fetchNextTriggerRef} isFetching={isFetchingNextPage} />
 */
export const PageListLoader = forwardRef<HTMLDivElement, Omit<PageListLoaderProps, 'ref'>>(
  ({ label, size = 'md' }, ref) => {
    const { t } = useTranslation()
    return (
      <div className="mt-4" ref={ref}>
        <Loader layout="horizontal" size={size} label={label ?? t('common.list.loadingMore')} />
      </div>
    )
  },
)

PageListLoader.displayName = 'Page.ListLoader'
